/**
 * Templatean Landing Page Scroll Utilities
 * Navbar scroll state, smooth anchor scrolling and scroll progress.
 * Reveal, stagger, hover and page-transition animations live in `@/components/motion`.
 */

import { useEffect, useRef, useState, RefObject } from 'react'

// ============================================
// TYPES
// ============================================

// ============================================
// REACT HOOKS
// ============================================

/**
 * Hook for scroll-based parallax effect
 *
 * Usage:
 * const { ref, progress } = useScrollProgress();
 * <div ref={ref} style={{ transform: `translateY(${progress * 50}px)` }}>
 */
export function useScrollProgress<T extends HTMLElement = HTMLDivElement>(): {
  ref: RefObject<T | null>
  progress: number
} {
  const ref = useRef<T | null>(null)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (prefersReducedMotion) return

    const handleScroll = () => {
      const rect = element.getBoundingClientRect()
      const windowHeight = window.innerHeight

      // Calculate progress from 0 (element at bottom) to 1 (element at top)
      const elementProgress = 1 - (rect.top / windowHeight)
      const clampedProgress = Math.max(0, Math.min(1, elementProgress))

      setProgress(clampedProgress)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return { ref, progress }
}

// ============================================
// NAVBAR SCROLL EFFECT
// ============================================

/**
 * Initialize navbar scroll effect (adds/removes 'landing-nav-scrolled' class)
 *
 * Usage:
 * <nav id="main-nav" className="landing-nav">...</nav>
 * initNavbarScroll('main-nav');
 */
export function initNavbarScroll(
  navId: string,
  threshold: number = 50
): () => void {
  const nav = document.getElementById(navId)
  if (!nav) return () => {}

  const handleScroll = () => {
    if (window.scrollY > threshold) {
      nav.classList.add('landing-nav-scrolled')
    } else {
      nav.classList.remove('landing-nav-scrolled')
    }
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll() // Initial check

  return () => window.removeEventListener('scroll', handleScroll)
}

/**
 * React hook for navbar scroll state
 *
 * Usage:
 * const isScrolled = useNavbarScroll(50);
 * <nav className={cn('landing-nav', isScrolled && 'landing-nav-scrolled')}>
 */
export function useNavbarScroll(threshold: number = 50): boolean {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > threshold)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [threshold])

  return isScrolled
}

// ============================================
// SMOOTH SCROLL
// ============================================

/**
 * Smooth scroll to element by ID
 * Accounts for fixed header height
 */
export function scrollToElement(
  elementId: string,
  offset: number = 0
): void {
  const element = document.getElementById(elementId)
  if (!element) return

  const headerHeight = parseInt(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--landing-header-height') || '72'
  )

  const elementPosition = element.getBoundingClientRect().top
  const offsetPosition = elementPosition + window.scrollY - headerHeight - offset

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })
}

/**
 * Initialize smooth scroll for anchor links
 */
export function initSmoothScroll(): () => void {
  const handleClick = (e: Event) => {
    const anchor = e.currentTarget as HTMLAnchorElement
    e.preventDefault()
    const href = anchor.getAttribute('href')
    if (href && href.length > 1 && href.startsWith('#')) {
      scrollToElement(href.substring(1))
    }
  }

  const anchors = document.querySelectorAll('a[href^="#"]')
  anchors.forEach((anchor) => {
    anchor.addEventListener('click', handleClick)
  })

  return () => {
    anchors.forEach((anchor) => {
      anchor.removeEventListener('click', handleClick)
    })
  }
}

// ============================================
// REACT HOOK: SMOOTH ANCHOR SCROLL
// ============================================

/**
 * Enables smooth scrolling for in-page anchor links for the lifetime of the layout.
 * Scroll reveal and stagger animations live in `@/components/motion`.
 */
export function useSmoothScroll(): void {
  useEffect(() => initSmoothScroll(), [])
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Get CSS variable value
 */
export function getCSSVariable(name: string): string {
  if (typeof window === 'undefined') return ''
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}
